import { geometryUtils } from '../geometryUtils';

import { clip } from './engine';
import type { ClipPolygon as PcPolygon } from './engine';
import {
	boundingBox,
	bboxOverlaps,
	fromPcRing,
	multiPolygonArea,
	ringLength,
	toPcRing,
} from './geometry';
import type { Ring } from './types';

/** What a layer's openings actually remove, for measurement and for display. */

/**
 * Area to subtract from `outer` for its openings.
 *
 * ALWAYS clips the openings to `outer`. An earlier version took a plain-sum fast
 * path when every opening's bounding box sat inside the outer bounding box and the
 * boxes were pairwise disjoint — which is unsound, because a bounding box cannot
 * distinguish "inside the box" from "inside the ring". For a CONCAVE outer ring —
 * i.e. every real floor-plan outline — an opening drawn on an edge sits comfortably
 * within the outer bbox while lying largely outside the region, and the fast path
 * removed its whole area. Observed on a real canvas: 6 subtracted where 2 was
 * correct, a 200% over-subtraction that under-measures the layer and so under-orders
 * material.
 *
 * So: union the openings (shared area counted once) and intersect with `outer` (any
 * part outside the region counts for nothing). The cost is bounded and proportional
 * to openings, not to layers: a ring set with none returns above without touching
 * geometry, which is the overwhelmingly common case and keeps
 * `computeConvertedMeasurements` — per layer per render, and again in every BOM
 * recalculation — free for uncut layers.
 *
 * This is also what makes measurement agree with rendering by construction, since
 * `clipHolesToRing` clips the same way.
 */
export const netHoleArea = (outer: Ring, holes: Ring[]): number => {
	if (holes.length === 0) return 0;

	const closed = holes.filter((hole) => geometryUtils.isPathClosed(hole));

	if (closed.length === 0) return 0;

	const [first, ...rest] = closed.map((hole) => [toPcRing(hole)] as PcPolygon);
	if (!first) return 0;

	const merged = rest.length > 0 ? clip.union(first, ...rest) : first;
	// A failed clip subtracts nothing, so the layer measures LARGER. That
	// over-orders material, which is the safe direction to be wrong in.
	if (merged === null) return 0;

	const clipped = clip.intersection(merged, [toPcRing(outer)]);

	return clipped === null ? 0 : multiPolygonArea(clipped);
};

/**
 * What each opening actually removes, index-aligned with `cutouts`.
 *
 * BOTH figures are effective, not as-drawn. Reporting a gross perimeter beside a
 * clipped area put two different semantics in one sidebar row — an opening drawn
 * across a boundary read as a large outline removing a small area, which does not
 * reconcile against anything else on screen.
 *
 * Summed across EVERY outer ring the opening overlaps, because one opening can span
 * two separate areas of the same layer and reduces each of them. An opening
 * overlapping nothing reports zero for both.
 *
 * Per-row figures are independent, so two overlapping openings each report their own
 * contribution and the rows do not sum to the layer's total reduction.
 * `netHoleArea` is the authority for that total; these are for display.
 */
export const effectiveCutoutMeasures = (
	paths: Ring[],
	cutouts: Ring[]
): { area: number; length: number }[] => {
	if (cutouts.length === 0) return [];

	const outerBoxes = paths.map((outer) => boundingBox(outer));

	return cutouts.map((cutout) => {
		if (!geometryUtils.isPathClosed(cutout)) {
			return { area: 0, length: ringLength(cutout) };
		}

		const cutoutBox = boundingBox(cutout);

		return paths.reduce(
			(total, outer, index) => {
				const box = outerBoxes[index];
				if (!box || !bboxOverlaps(box, cutoutBox)) return total;

				const clipped = clip.intersection([toPcRing(cutout)] as PcPolygon, [
					toPcRing(outer),
				]);
				// This row reports what the opening removes; if the clip fails it
				// contributes nothing rather than guessing.
				if (clipped === null) return total;

				return {
					area: total.area + multiPolygonArea(clipped),
					length:
						total.length +
						clipped.reduce(
							(sum, polygon) =>
								sum +
								polygon.reduce((ringSum, ring) => {
									const converted = fromPcRing(ring);
									return converted ? ringSum + ringLength(converted) : ringSum;
								}, 0),
							0
						),
				};
			},
			{ area: 0, length: 0 }
		);
	});
};
